import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    FormHelperText, Button, Tooltip, IconButton, Typography
} from '@material-ui/core';
import useFormularioInscricao from './useFormularioInscricao';
import * as Yup from "yup";
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import DialogAdicionarDocumento from './DialogAdicionarDocumento.jsx';
import DialogVisualizarPendencias from './DialogVisualizarPendencias.jsx';
import useDialogState from '../../../../hooks/useDialogState.js';
import useFetch from "../../../../hooks/useFetch.js";
import { URL_PROCESSO_SELETIVO } from "../../../../constants";
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import Add from '@material-ui/icons/AddOutlined';
import Delete from '@material-ui/icons/DeleteOutlined';
import Warning from '@material-ui/icons/WarningOutlined';
import CheckCircle from '@material-ui/icons/CheckCircleOutlined';
import { containedButton, tituloSecao } from "../../../../utils/styles";
import ContainedButton from '../../../shared/buttons/ContainedButton';
import StatusDocumento from '../../../shared/statusdocumento/StatusDocumento.js';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import { criarColunaAcoes } from './AcoesLinha.jsx';
import useConfirmationDialog from "../../../shared/confirmationdialog/useConfirmationDialog";
import useErrorMessage from "../../../../hooks/useErrorMessage";
import { isSameMember } from './membroFamiliar';
import { arquivoParaBase64 } from '../../../../utils/arquivo';
import {
    isAnexoAprovado, pendenciasEmAberto, podeRemoverAnexo, tamanhoEnvioBase64,
    truncarDescricao, LIMITE_ENVIO_BASE64
} from './documentosRegras';

const useStyles = makeStyles(theme => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    tableWrapper: {
        marginBottom: theme.spacing(3)
    },
    helperText: {
        marginTop: theme.spacing(2),
        fontSize: '0.9rem'
    },
    tabPanel: {
        padding: theme.spacing(1, 0)
    },
    tabLabel: {
        display: 'inline-flex',
        alignItems: 'center'
    },
    pendingTabIcon: {
        color: theme.palette.warning.main,
        marginLeft: theme.spacing(1)
    },

    ...containedButton(theme, false, false)
}));

/*
 * Sem validação de documentos "obrigatórios": o campo `doc.obrigatorio` que a
 * regra usava não existe em nenhum documento vindo do back. `documentosExigidos`
 * e `permitirEntregaDocumentos` continuam nos parâmetros porque
 * `inscricaoSchemas.js` chama esta função com os dois.
 */
export const criarSchemaDocumentos = (documentosExigidos, permitirEntregaDocumentos) => Yup.object();

const FormDocumentos = forwardRef((props, ref) => {
    const classes = useStyles();
    const showConfirmation = useConfirmationDialog();
    const showErrorMessage = useErrorMessage();
    const { data, onUpdate, focusPendingTab = false } = props;
    const documentosExigidos = data.documentosExigidos || [];

    const {
        dialogState,
        openDialog,
        closeDialog
    } = useDialogState({
        adicionarDocumento: false,
        visualizarPendentes: false
    });

    const [uploadDialogInitialValues, setUploadDialogInitialValues] = useState({});
    const [selectedPendingDocs, setSelectedPendingDocs] = useState([]);
    const [selectedPendingMemberName, setSelectedPendingMemberName] = useState('');
    const pendingTabFocusedRef = useRef(false);

    const tabsRef = useRef(null);

    const permitirEntregaDocumentos = data.permitirEntregaDocumentos ?? false;
    const permitirEntregaDocumentoPendentes = data.permitirEntregaDocumentoPendentes ?? false;

    const formEtapa = useFormularioInscricao({
        valoresIniciais: {
            inscricaoAnexos: data.inscricaoAnexos || []
        },
        reinicializar: true,
        schema: criarSchemaDocumentos(documentosExigidos, permitirEntregaDocumentos)
    });

    useImperativeHandle(ref, () => ({
        formulario: formEtapa.api
    }));

    /*
     * As pendências em aberto não moram mais em estado próprio: são calculadas
     * a cada render a partir da pendência original (`data`, nunca alterada por
     * aqui) e dos anexos atuais do formulário. Enviar um documento novo para
     * uma pendência a resolve (o anexo carrega `idInscricaoPendenciaAnexo`);
     * remover esse envio a traz de volta, sem precisar mexer em nenhum estado
     * separado.
     */
    const pendenciasAbertas = pendenciasEmAberto(data.inscricaoPendenciaAnexos || [], formEtapa.valores.inscricaoAnexos || []);

    /*
     * Leva os anexos para a inscrição na própria ação (adicionar ou remover),
     * não num useEffect: pelo efeito, o onUpdate re-renderizava, a lista
     * voltava por `data`, o formulário reiniciava e o efeito disparava de novo
     * (Maximum update depth exceeded).
     */
    const atualizarDocumentos = (anexos) => {
        formEtapa.definirValor('inscricaoAnexos', anexos);
        onUpdate({ inscricaoAnexos: anexos });
    };

    const handleOpenAdicionarParaPessoa = (familiarIndex) => {
        setUploadDialogInitialValues({
            selectedPendingDoc: null,
            selectedDocType: '',
            desc: '',
            selectedFamiliarIndex: String(familiarIndex)
        });
        openDialog('adicionarDocumento')();
    };

    const handleOpenAdicionarPendente = (pendingItem) => {
        let personIndex = -1;
        if (pendingItem.composicaoFamiliar) {
            personIndex = (data.composicaoFamiliar || []).findIndex(
                member => isSameMember(member, pendingItem.composicaoFamiliar)
            );

            // Membro removido do grupo familiar depois que a pendência foi gerada: não anexa ao candidato por engano.
            if (personIndex === -1) {
                showErrorMessage('O integrante desta pendência não está mais no grupo familiar. Procure a instituição.');
                return;
            }
        }

        setUploadDialogInitialValues({
            selectedPendingDoc: pendingItem,
            selectedDocType: pendingItem.documento?.id || '',
            desc: pendingItem.descricao || '',
            selectedFamiliarIndex: personIndex !== -1 ? String(personIndex) : "-1"
        });

        // Alterna para a aba do integrante correspondente usando ref
        if (tabsRef.current) {
            tabsRef.current.setTabIndex(personIndex + 1);
        }

        closeDialog('visualizarPendentes')();
        openDialog('adicionarDocumento')();
    };

    const getPendingDocsForMember = (member) => pendenciasAbertas.filter(pendingDoc => {
        if (!member) return !pendingDoc.composicaoFamiliar;

        return pendingDoc.composicaoFamiliar
            && isSameMember(pendingDoc.composicaoFamiliar, member);
    });

    const getMemberLabel = (member) => member
        ? `${member.descricao} (${member.grauParentesco?.descricao || member.parentescoDesc || ''})`
        : (data.pessoa?.nome || 'Candidato (Eu)');

    const handleOpenVisualizarPendencias = (member) => {
        setSelectedPendingDocs(getPendingDocsForMember(member));
        setSelectedPendingMemberName(getMemberLabel(member));
        openDialog('visualizarPendentes')();
    };

    const handleSaveDocumento = async (values) => {
        const { selectedDocType, selectedFamiliarIndex, desc, selectedFile, selectedPendingDoc } = values;
        // Pendência com documento fora de documentosExigidos: usa o documento da própria pendência.
        const documento = selectedPendingDoc?.documento || documentosExigidos.find(d => d.id === parseInt(selectedDocType, 10));

        if (!documento || !selectedFile) {
            showErrorMessage('Selecione o tipo de documento e o arquivo.');
            return;
        }

        let base64Data = null;
        try {
            base64Data = await arquivoParaBase64(selectedFile);
        } catch (e) {
            showErrorMessage('Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.');
        }

        if (!base64Data) {
            return;
        }

        const anexosAtuais = formEtapa.valores.inscricaoAnexos || [];
        const familiarObj = selectedFamiliarIndex === '-1' ? null : (data.composicaoFamiliar?.[parseInt(selectedFamiliarIndex, 10)] || null);

        const novoAnexo = {
            idInscricaoPendenciaAnexo: selectedPendingDoc?.id || null,
            documento,
            descricao: truncarDescricao(desc),
            anexo: {
                nome: selectedFile.name,
                mimetype: selectedFile.type,
                tamanho: selectedFile.size,
                arquivo: base64Data
            },
            composicaoFamiliar: familiarObj,
            inscricaoAnexoAvaliacaoList: [] // Envio novo ainda não tem avaliação.
        };

        const anexosComNovo = [...anexosAtuais, novoAnexo];
        if (tamanhoEnvioBase64(anexosComNovo, data.inscricaoEnem) > LIMITE_ENVIO_BASE64) {
            showErrorMessage('Os documentos novos passam de 8 MB. Salve os que já adicionou e depois envie os demais.');
            return;
        }

        /*
         * Não precisa tratar a pendência à parte: como ela é calculada a partir
         * dos anexos (pendenciasAbertas), o novo anexo com
         * `idInscricaoPendenciaAnexo` já a tira da lista sozinho.
         */
        atualizarDocumentos(anexosComNovo);

        closeDialog('adicionarDocumento')();
    };

    const handleRemove = (index) => {
        showConfirmation('Deseja realmente remover este documento?', () => {
            const current = [...(formEtapa.valores.inscricaoAnexos || [])];
            current.splice(index, 1);
            atualizarDocumentos(current);
        });
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const getColumns = (rows) => {
        const uploadedDocNames = Array.from(new Set(
            (rows || [])
                .map(anexo => anexo.documento?.nome)
                .filter(Boolean)
        )).sort();

        return [
            {
                label: 'Documento',
                field: 'documento.nome',
                filterable: true,
                filterOptions: uploadedDocNames.map(name => ({ value: name, label: name })),
                customFilter: (filterTerm, value) => {
                    if (!filterTerm) return true;
                    return value === filterTerm;
                }
            },
            { label: 'Descrição', field: 'descricao', valueGetter: (val) => (val && String(val).trim()) || '-' },
            { label: 'Nome do Arquivo', field: 'anexo.nome' },
            { label: 'Tipo', field: 'anexo.mimetype' },
            { label: 'Tamanho', field: 'anexo.tamanho', valueGetter: (val) => formatBytes(val) },
            {
                label: 'Status',
                filterable: true,
                filterOptions: [
                    { value: 'Aprovado', label: 'Aprovado' },
                    { value: 'Em Análise', label: 'Em Análise' }
                ],
                customFilter: (filterTerm, value, row) => {
                    if (!filterTerm) return true;
                    const statusText = isAnexoAprovado(row) ? 'Aprovado' : 'Em Análise';
                    return statusText === filterTerm;
                },
                searchValue: row => isAnexoAprovado(row) ? 'Aprovado' : 'Em Análise',
                valueGetter: (val, row) => {
                    const status = isAnexoAprovado(row) ? 'A' : 'E';
                    return <StatusDocumento status={status} />;
                }
            },
            criarColunaAcoes(row => {
                const idx = (formEtapa.valores.inscricaoAnexos || []).indexOf(row);
                /* Envio novo sempre pode sair; salvo só com a entrega aberta e sem estar aprovado. */
                return [
                    {
                        titulo: 'Remover',
                        icone: <Delete fontSize="small" />,
                        onClick: () => handleRemove(idx),
                        visivel: podeRemoverAnexo(row, { permitirEntregaDocumentos })
                    }
                ];
            })
        ];
    };



    const temPeriodoAberto = permitirEntregaDocumentos || permitirEntregaDocumentoPendentes;
    const anexos = formEtapa.valores.inscricaoAnexos || [];

    const anexosDoInscrito = anexos.filter(
        anexo => !anexo.composicaoFamiliar
    );

    const anexosDaComposicao = (member) => anexos.filter(
        anexo => anexo.composicaoFamiliar
            && isSameMember(anexo.composicaoFamiliar, member)
    );

    const getTabLabel = (label, member) => {
        const possuiPendencia = getPendingDocsForMember(member).length > 0;

        return (
            <span className={classes.tabLabel}>
                {label}
                {possuiPendencia && (
                    <Tooltip title="Este integrante possui documento pendente">
                        <Warning className={classes.pendingTabIcon} fontSize="small" />
                    </Tooltip>
                )}
            </span>
        );
    };

    const renderTabContent = (personIndex, member, rows) => {
        const pendingDocsForMember = getPendingDocsForMember(member);

        return (
            <div className={classes.tabPanel}>
                <div className={classes.tableWrapper}>
                    <DataGrid
                        responsive="card"
                        toolbarContent={
                            (permitirEntregaDocumentos || (permitirEntregaDocumentoPendentes && pendingDocsForMember.length > 0)) && (
                                <>
                                    {permitirEntregaDocumentos && (
                                        <Button
                                            variant="contained"
                                            className={classes.containedButton}
                                            startIcon={<Add />}
                                            onClick={() => handleOpenAdicionarParaPessoa(personIndex)}
                                        >
                                            Adicionar
                                        </Button>
                                    )}
                                    {permitirEntregaDocumentoPendentes && pendingDocsForMember.length > 0 && (
                                        <ContainedButton
                                            startIcon={<Warning />}
                                            onClick={() => handleOpenVisualizarPendencias(member)}
                                        >
                                            Adicionar Pendências
                                        </ContainedButton>
                                    )}
                                </>
                            )
                        }
                        filter
                        mobileFilterByColumn={false}
                        filterOptions={{
                            filterByColumn: true
                        }}
                        tableContainerProps={{
                            component: 'div'
                        }}
                        rows={rows}
                        columns={getColumns(rows)}
                        comparatorKeys={['documento.nome']}
                        paginate
                        paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                        noRecordsText="Nenhum documento anexado para este integrante."
                    />
                </div>
            </div>
        );
    };

    const tabContent = [
        {
            label: getTabLabel(data.pessoa?.nome || 'Candidato (Eu)', null),
            content: renderTabContent(-1, null, anexosDoInscrito)
        },
        ...(data.composicaoFamiliar || []).map((member, idx) => ({
            label: getTabLabel(getMemberLabel(member), member),
            content: renderTabContent(idx, member, anexosDaComposicao(member))
        }))
    ];

    useEffect(() => {
        if (!focusPendingTab) {
            pendingTabFocusedRef.current = false;
            return;
        }

        if (pendingTabFocusedRef.current || !pendenciasAbertas.length || !tabsRef.current) {
            return;
        }

        const membersInTabOrder = [null, ...(data.composicaoFamiliar || [])];
        const firstPendingTabIndex = membersInTabOrder.findIndex(member => getPendingDocsForMember(member).length > 0);

        tabsRef.current.setTabIndex(firstPendingTabIndex >= 0 ? firstPendingTabIndex : 0);
        pendingTabFocusedRef.current = true;
    }, [focusPendingTab, pendenciasAbertas, data.composicaoFamiliar]);

    return (
        <form className={classes.root} noValidate autoComplete="off">
            {(!temPeriodoAberto || (pendenciasAbertas.length > 0 && !permitirEntregaDocumentoPendentes)) && (
                <InstructionMessage
                    title="Atenção!"
                    type="warning"
                    variant="standard"
                    items={[
                        ...(!temPeriodoAberto ? ["O Período de entrega de documentos não está disponível."] : []),
                        ...(pendenciasAbertas.length > 0 && !permitirEntregaDocumentoPendentes
                            ? ["O período de entrega de documentos pendentes não está aberto. Os documentos pendentes não podem ser enviados no momento."]
                            : [])
                    ]}
                />
            )}

            {pendenciasAbertas.length > 0 && (
                <>
                    <InstructionMessage
                        title="Orientações sobre Documentos Pendentes"
                        type="warning"
                        variant="standard"
                        collapsible={true}
                        defaultExpanded={true}
                        items={[
                            `Você possui ${pendenciasAbertas.length} documento(s) pendente(s) de correção nesta inscrição.`,
                            "As pendências estão separadas por integrante nas abas abaixo.",
                            permitirEntregaDocumentoPendentes
                                ? "Na aba do integrante, clique em Adicionar Pendências para visualizar e enviar somente os documentos pendentes dele, conforme a orientação informada."
                                : "O período de entrega de documentos pendentes não está aberto. Assim que for liberado, os documentos pendentes poderão ser enviados nesta etapa."
                        ]}
                    />
                </>
            )}

            <InstructionMessage
                title="Orientações sobre Documentos Enviados"
                type="info"
                variant="standard"
                collapsible={true}
                items={[
                    "Todos os documentos anexados encontram-se listados abaixo divididos por integrante familiar em abas.",
                    permitirEntregaDocumentos ? "É possível remover documentos que ainda estão \"Em Análise\" e submeter novos documentos atualizados." : "O período de alteração de documentos está fechado.",
                    "Não é possível fazer nenhuma alteração em documentos que já foram avaliados e \"Aprovados\"."
                ]}
            />

            <GenericTab
                ref={tabsRef}
                mobileVariant="accordion"
                scrollButtons="auto"
                content={tabContent}
            />

            {formEtapa.tocados.inscricaoAnexos && formEtapa.erros.inscricaoAnexos && (
                <FormHelperText error className={classes.helperText}>
                    {formEtapa.erros.inscricaoAnexos}
                </FormHelperText>
            )}

            <DialogAdicionarDocumento
                open={dialogState.adicionarDocumento}
                onClose={closeDialog('adicionarDocumento')}
                onSave={handleSaveDocumento}
                pessoaNome={data.pessoa?.nome}
                composicaoFamiliar={data.composicaoFamiliar}
                disponiveis={documentosExigidos}
                initialValues={uploadDialogInitialValues}
            />

            <DialogVisualizarPendencias
                open={dialogState.visualizarPendentes}
                onClose={closeDialog('visualizarPendentes')}
                pendingDocs={selectedPendingDocs}
                memberName={selectedPendingMemberName}
                onInsertPendingDoc={handleOpenAdicionarPendente}
            />
        </form>
    );
});

FormDocumentos.displayName = 'FormDocumentos';
export default FormDocumentos;
